import { Alert, Box, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { DECISION_META, type Breakdown, type HvDecision } from '@hv/shared';
import { useDashboard } from '../api/hooks';
import { describeError } from '../components/Notify';
import { ReferralDashboardSection } from '../referrals/ReferralDashboardSection';
import { BAND_COLORS, BRAND, DECISION_COLORS } from '../theme';
import { useCycleParam } from '../urlState';

function Card({ label, value, sub }: { label: string; value: number; sub?: string }) {
  return (
    <Paper sx={{ p: 2.5 }} data-testid={`card-${label}`}>
      <Typography variant="h4" component="h3">
        {label}
      </Typography>
      <Typography sx={{ fontSize: 32, fontWeight: 800, color: BRAND.navy, lineHeight: 1.2, mt: 0.5 }}>{value}</Typography>
      {sub && (
        <Typography variant="body2" color="text.secondary">
          {sub}
        </Typography>
      )}
    </Paper>
  );
}

function Bars({ title, data, color }: { title: string; data: Breakdown; color: (key: string) => string }) {
  const max = Math.max(1, ...data.map((d) => d.count));
  return (
    <Paper sx={{ p: 2.5 }}>
      <Typography variant="h3" component="h3" mb={2}>
        {title}
      </Typography>
      <Stack gap={1.25} component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
        {data.map((d) => (
          <Box component="li" key={d.key} sx={{ display: 'grid', gridTemplateColumns: '170px 1fr 40px', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="body2">{d.label}</Typography>
            <Box sx={{ bgcolor: '#eceff3', borderRadius: 1.5, height: 12, overflow: 'hidden' }} aria-hidden>
              <Box sx={{ width: `${(d.count / max) * 100}%`, height: '100%', bgcolor: color(d.key) }} />
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'right' }}>
              {d.count}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Paper>
  );
}

export function DashboardPage() {
  const dash = useDashboard(useCycleParam());
  if (dash.error) return <Alert severity="error" sx={{ m: 2 }}>Could not load dashboard: {describeError(dash.error)}</Alert>;
  const d = dash.data;
  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h2" mb={2}>
        Pipeline Dashboard
      </Typography>
      {!d ? (
        <Skeleton variant="rounded" height={320} />
      ) : (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 2, mb: 2 }}>
            <Card label="Total households" value={d.totals.total} />
            <Card label="High visibility" value={d.totals.highVisibility} />
            <Card label="Major+ capacity" value={d.totals.majorPlusCapacity} />
            <Card label="Follow-ups due ≤7d" value={d.totals.followUpsDueSoon} sub={`${d.totals.overdueFollowUps} overdue`} />
            <Card label="Pending scoring" value={d.totals.pendingScoring} />
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2 }}>
            <Bars title="Decision breakdown (applicants)" data={d.decision} color={(k) => DECISION_COLORS[DECISION_META[k as HvDecision].color].dot} />
            <Bars title="HV score distribution" data={d.hvDistribution} color={(k) => BAND_COLORS[k as keyof typeof BAND_COLORS] ?? BRAND.grey} />
            <Bars title="Recommendation source" data={d.recommendationSource} color={() => BRAND.navy} />
            <Bars title="Giving capacity pipeline" data={d.capacity} color={() => BRAND.grey} />
          </Box>
          {d.referrals && <ReferralDashboardSection data={d.referrals} cycleId={d.cycleId} />}
        </>
      )}
    </Box>
  );
}
